import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isVideoUrl } from '../../Services/ApiService';
import SystemDataService from '../../Services/SystemDataService';
import Image from '../../Utils/Image/image-component';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate';
import VideoComponent from '../../Utils/Video/video-component';
import ContactFormLightweight from '../../Utils/Forms/ContactForm/contact-form-lightweight-component.js';
import { useNavigate } from 'react-router';

export default function LandingHeroSection({ heroContent }) {
    const scrollOrNavigate = useScrollOrNavigate();
    const navigate = useNavigate();
    const systemData = useSelector((state) => state.systemData || {});
    const { dir } = systemData;

    const [carouselItems, setCarouselItems] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const {
        heroTitle = "PROFESSIONAL DOCK CONSTRUCTION & BOAT LIFT INSTALLATION.",
        heroTagline = "CAPE CORAL'S PREFERRED MARINE CONTRACTOR",
        heroDescription = "Honest, dependable marine construction services for waterfront property owners throughout Southwest Florida.",
        heroMedia,
    } = heroContent || {};

    useEffect(() => {
        async function fetchCarousel() {
            try {
                const items = await SystemDataService.getCarouselImages();
                if (items && items.length > 0) {
                    setCarouselItems(items);
                }
            } catch (err) {
                console.error("Failed to load carousel images", err);
            }
        }
        fetchCarousel();
    }, []);

    // Pause on video slides — advance after fixed duration for images, longer for videos
    useEffect(() => {
        if (!carouselItems.length) return;
        const current = carouselItems[currentIndex];
        const isVideo = isVideoUrl(current?.image || '');
        const delay = isVideo ? 8000 : 5000;
        const id = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
        }, delay);
        return () => clearTimeout(id);
    }, [currentIndex, carouselItems]);

    const isHeroVideo = heroMedia && isVideoUrl(heroMedia);

    const renderTitle = (text) => {
        if (!text) return null;
        const words = text.split(' ');
        const half = Math.ceil(words.length / 2);
        const line1 = words.slice(0, half).join(' ');
        const line2 = words.slice(half).join(' ');
        return (
            <>
                <span className="text-white">{line1}</span>
                <br />
                <span className="text-secondary-400">{line2}</span>
            </>
        );
    };

    return (
        <div key={dir} dir={dir} className="relative w-full flex flex-col items-center bg-black overflow-hidden">

            <div className="relative w-full flex justify-center h-full min-h-[80vh]">

                {/* Carousel background */}
                <div className="absolute inset-0 w-full h-full z-0 bg-black">
                    {carouselItems.length > 0 ? (
                        carouselItems.map((item, index) => {
                            const src = item?.image || item;
                            const isCurrent = index === currentIndex;
                            const isVideo = isVideoUrl(src);
                            return (
                                <div
                                    key={index}
                                    className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${isCurrent ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                                >
                                    {isVideo ? (
                                        <VideoComponent
                                            video={src}
                                            mobile={true}
                                            className="w-full h-full object-cover object-center"
                                        />
                                    ) : (
                                        <Image
                                            src={src}
                                            quality={90}
                                            alt={`Slide ${index}`}
                                            className="w-full h-full object-cover object-center"
                                            wrapperClassName="w-full h-full"
                                        />
                                    )}
                                </div>
                            );
                        })
                    ) : heroMedia ? (
                        isHeroVideo ? (
                            <VideoComponent
                                video={heroMedia}
                                mobile={true}
                                autoPlay
                                muted
                                loop
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <Image
                                src={heroMedia}
                                quality={90}
                                alt="Hero Background"
                                className="w-full h-full object-cover object-center"
                                wrapperClassName="w-full h-full"
                            />
                        )
                    ) : null}
                </div>

                {/* Dark overlay — black at top fading to transparent */}
                <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-b from-black via-black/50 to-transparent" />

                {/* Content */}
                <div className="relative z-30 w-full max-w-[1400px] mx-auto px-6 lg:px-8 flex items-center py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">

                        {/* Left: Text */}
                        <div className="flex flex-col space-y-6 text-left items-start">
                            {heroTagline && (
                                <div className="bg-secondary-400 text-white uppercase text-xs font-bold tracking-widest py-1.5 px-3 w-fit">
                                    {heroTagline}
                                </div>
                            )}

                            {heroTitle && (
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-tight">
                                    {renderTitle(heroTitle)}
                                </h1>
                            )}

                            <p className="text-base md:text-lg text-gray-300 max-w-xl leading-relaxed">
                                {heroDescription}
                            </p>

                            <button
                                onClick={() => scrollOrNavigate(null, 'services', '/services')}
                                className="border-2 border-secondary-400 text-secondary-400 hover:bg-secondary-400 hover:text-white font-bold text-sm px-8 py-3 transition-colors uppercase tracking-widest mt-2"
                            >
                                View Our Services
                            </button>
                        </div>

                        {/* Right: Lightweight form card */}
                        <div className="w-full flex justify-center lg:justify-end">
                            <div className="w-full max-w-md bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl">
                                <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-1">
                                    Get a Free Estimate
                                </h3>
                                <p className="text-white/60 text-sm mb-6">
                                    Tell us about your project — we'll respond same day.
                                </p>
                                <ContactFormLightweight onSuccess={() => navigate('/thank-you')} />
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
